import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Printer, Lightbulb } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const CONTENT = {
  en: {
    page_title: "Lighting Basics",
    page_sub: "Photography literally means 'drawing with light'. Before you memorize named setups or buy a single softbox, master four fundamentals: where the light comes from, how soft it is, how far away it sits, and what color it is. Get these right and any scene becomes readable.",
    download: "Download as PDF",
    generating: "Generating...",

    diagram_title: "The Three-Point Lighting System",
    diagram_caption: "The classic studio foundation: a Key light (main), a Fill light (softens shadows), and a Rim/Back light (separates the subject from the background). Master this and you can light almost anything.",
    diag_subject: "Subject",
    diag_camera: "Camera",
    diag_key: "Key",
    diag_fill: "Fill",
    diag_rim: "Rim / Back",

    three_title: "1. The three lights, explained",
    three: [
      { n: "Key Light", d: "Your main, brightest light. It defines the shape of the face and casts the primary shadows. Usually placed at roughly 45° to one side of the subject and slightly above eye level. Everything else supports the key." },
      { n: "Fill Light", d: "A softer, dimmer light on the OPPOSITE side of the key. It doesn't create new shadows — it lifts the dark ones so detail is visible. The key-to-fill brightness difference is your 'lighting ratio': a big difference = dramatic and moody; a small difference = soft and even." },
      { n: "Rim / Back Light", d: "Placed behind the subject, aimed at the back of the head/shoulders. It creates a bright edge that separates the subject from the background, adding depth and a professional 3D look. Also called a hair light or kicker." },
    ],

    quality_title: "2. Hard vs soft light — the most important concept",
    quality_body: "The single biggest factor in how light looks is its QUALITY, not its brightness. Hard light comes from a small source and creates sharp, dark-edged shadows (midday sun, a bare bulb, a direct flash). Soft light comes from a large source and creates gentle, gradual shadows (an overcast sky, a big window, a softbox).",
    quality_rule: "The rule that controls everything: the LARGER the light source relative to your subject — and the CLOSER it is — the softer the light. That's why a small softbox up close is softer than a big one far away. To soften any light: make it bigger (add a softbox/umbrella) or bring it closer.",

    direction_title: "3. The direction of light",
    direction: [
      "Front light (light near the camera): flat, even, hides texture and shadows — flattering but can look lifeless.",
      "Side light (45°-90°): reveals texture and shape, adds drama and dimension — the most-used portrait direction.",
      "Backlight (behind the subject): creates silhouettes, glowing rim light, and haze — beautiful but tricky to expose.",
      "Top light (above): natural for overhead sun; can create unflattering eye shadows ('raccoon eyes') without fill.",
    ],

    inverse_title: "4. The Inverse Square Law (why distance matters so much)",
    inverse_body: "Light falls off fast. Double the distance from light to subject and you get only a QUARTER of the brightness (not half). Practical consequences: move a light closer and it gets both brighter AND softer; place two people at different distances from one light and the near one will be much brighter. Use this to control fall-off — keep subjects equidistant for even light, or use distance to spotlight one person.",

    color_title: "5. Color temperature & white balance",
    color_body: "Light has color, measured in Kelvin (K). Candlelight and tungsten bulbs are warm/orange (~2700-3200K); midday sun and flash are neutral (~5500K); shade and overcast are cool/blue (~7000K+). Your white balance setting tells the camera what 'white' is so colors look correct. Mixing light sources of different temperatures (warm room lamp + cool window) is the usual cause of weird color casts — match your sources or correct in RAW.",

    natural_title: "6. Continuous vs flash, natural vs artificial",
    natural_body: "Continuous light (the sun, LED panels) shows you exactly what you'll get — what you see is what you get. Flash/strobe fires a powerful burst, freezing motion and overpowering ambient light, but you must visualize the result. Natural window light is the best free 'softbox' there is: place your subject beside a large window, turn them toward it, and you have soft, directional light for free. When natural light runs out, that's when modifiers and kits (next lesson) come in.",

    next_more: "Ready to go deeper? Next, learn the named portrait setups and studio kits:",
    next_link: "Lighting Techniques",

    pro_tip: "Pro tip: before adding a second light, master a single light. Move one source around your subject — closer, farther, higher, to the side — and watch the shadows. 80% of great lighting is just one well-placed soft source plus a reflector for fill.",
  },

  es: {
    page_title: "Iluminacion Basica",
    page_sub: "Fotografia significa literalmente 'dibujar con luz'. Antes de memorizar montajes con nombre o comprar un softbox, domina cuatro fundamentos: de donde viene la luz, que tan suave es, que tan lejos esta y de que color es. Hazlo bien y cualquier escena se vuelve legible.",
    download: "Descargar como PDF",
    generating: "Generando...",

    diagram_title: "El Sistema de Iluminacion de Tres Puntos",
    diagram_caption: "La base clasica de estudio: una luz Principal (Key), una de Relleno (Fill, suaviza sombras) y una de Borde/Contra (Rim/Back, separa al sujeto del fondo). Domina esto y podras iluminar casi cualquier cosa.",
    diag_subject: "Sujeto",
    diag_camera: "Camara",
    diag_key: "Principal",
    diag_fill: "Relleno",
    diag_rim: "Borde / Contra",

    three_title: "1. Las tres luces, explicadas",
    three: [
      { n: "Luz Principal (Key)", d: "Tu luz principal y mas brillante. Define la forma del rostro y proyecta las sombras primarias. Suele colocarse a unos 45° a un lado del sujeto y un poco por encima del nivel de los ojos. Todo lo demas apoya a la principal." },
      { n: "Luz de Relleno (Fill)", d: "Una luz mas suave y tenue en el lado OPUESTO a la principal. No crea sombras nuevas — aclara las oscuras para que se vea el detalle. La diferencia de brillo entre principal y relleno es tu 'ratio de iluminacion': mucha diferencia = dramatico; poca diferencia = suave y parejo." },
      { n: "Luz de Borde / Contra (Rim/Back)", d: "Colocada detras del sujeto, apuntando a la nuca/hombros. Crea un borde brillante que separa al sujeto del fondo, añadiendo profundidad y un aspecto 3D profesional. Tambien llamada luz de cabello o kicker." },
    ],

    quality_title: "2. Luz dura vs suave — el concepto mas importante",
    quality_body: "El mayor factor en como se ve la luz es su CALIDAD, no su brillo. La luz dura viene de una fuente pequeña y crea sombras nitidas y de borde marcado (sol de mediodia, foco desnudo, flash directo). La luz suave viene de una fuente grande y crea sombras suaves y graduales (cielo nublado, una ventana grande, un softbox).",
    quality_rule: "La regla que controla todo: cuanto MAS GRANDE sea la fuente de luz en relacion al sujeto — y cuanto MAS CERCA este — mas suave es la luz. Por eso un softbox pequeño y cercano es mas suave que uno grande y lejano. Para suavizar cualquier luz: hazla mas grande (softbox/paraguas) o acercala.",

    direction_title: "3. La direccion de la luz",
    direction: [
      "Luz frontal (cerca de la camara): plana, pareja, oculta textura y sombras — favorecedora pero puede verse sin vida.",
      "Luz lateral (45°-90°): revela textura y forma, añade drama y dimension — la direccion mas usada en retrato.",
      "Contraluz (detras del sujeto): crea siluetas, borde luminoso y bruma — hermosa pero dificil de exponer.",
      "Luz cenital (desde arriba): natural con el sol; sin relleno puede crear sombras feas en los ojos ('ojos de mapache').",
    ],

    inverse_title: "4. La Ley del Cuadrado Inverso (por que importa tanto la distancia)",
    inverse_body: "La luz cae rapido. Duplica la distancia de la luz al sujeto y obtienes solo una CUARTA parte del brillo (no la mitad). Consecuencias practicas: acerca una luz y se vuelve mas brillante Y mas suave; coloca dos personas a distintas distancias de una luz y la cercana sera mucho mas brillante. Usa esto para controlar la caida — manten a los sujetos equidistantes para luz pareja, o usa la distancia para destacar a una persona.",

    color_title: "5. Temperatura de color y balance de blancos",
    color_body: "La luz tiene color, medido en Kelvin (K). La luz de vela y los focos de tungsteno son calidos/naranjas (~2700-3200K); el sol de mediodia y el flash son neutros (~5500K); la sombra y el cielo nublado son frios/azules (~7000K+). El balance de blancos le dice a la camara que es 'blanco' para que los colores se vean correctos. Mezclar fuentes de distinta temperatura (lampara calida + ventana fria) es la causa habitual de dominantes de color raras — iguala tus fuentes o corrige en RAW.",

    natural_title: "6. Continua vs flash, natural vs artificial",
    natural_body: "La luz continua (el sol, paneles LED) te muestra exactamente lo que obtendras — lo que ves es lo que hay. El flash/estrobo dispara un destello potente, congelando el movimiento y dominando la luz ambiente, pero debes visualizar el resultado. La luz natural de ventana es el mejor 'softbox' gratis que existe: coloca a tu sujeto junto a una ventana grande, girado hacia ella, y tienes luz suave y direccional gratis. Cuando la luz natural se acaba, ahi entran los modificadores y kits (siguiente leccion).",

    next_more: "¿Listo para profundizar? Luego aprende los montajes de retrato con nombre y los kits de estudio:",
    next_link: "Tecnicas de Iluminacion",

    pro_tip: "Tip pro: antes de añadir una segunda luz, domina una sola. Mueve una fuente alrededor de tu sujeto — mas cerca, mas lejos, mas alto, al lado — y observa las sombras. El 80% de la gran iluminacion es solo una fuente suave bien colocada mas un reflector para el relleno.",
  },
};

function ThreePointDiagram({ c }) {
  return (
    <svg viewBox="0 0 360 250" className="w-full h-auto" role="img" aria-label={c.diagram_title} data-testid="lighting-basics-svg">
      <rect x="0" y="0" width="360" height="250" fill="#0A0A0A" rx="8" />
      {/* subject */}
      <circle cx="180" cy="125" r="20" fill="#27272A" stroke="#F5A524" strokeWidth="2" />
      <text x="180" y="129" fill="#F5A524" fontSize="10" textAnchor="middle">{c.diag_subject}</text>
      {/* camera */}
      <rect x="165" y="210" width="30" height="20" fill="#18181B" stroke="#52525B" strokeWidth="2" rx="3" />
      <text x="180" y="246" fill="#A1A1AA" fontSize="10" textAnchor="middle">{c.diag_camera}</text>
      <line x1="180" y1="210" x2="180" y2="150" stroke="#52525B" strokeWidth="1.5" strokeDasharray="3 3" />
      {/* key light - upper left */}
      <circle cx="70" cy="60" r="13" fill="#F5A524" />
      <text x="70" y="40" fill="#F5A524" fontSize="11" textAnchor="middle">{c.diag_key}</text>
      <line x1="82" y1="68" x2="163" y2="116" stroke="#F5A524" strokeWidth="2.5" />
      {/* fill light - upper right (dimmer) */}
      <circle cx="290" cy="70" r="11" fill="#FCD34D" opacity="0.55" />
      <text x="290" y="48" fill="#FCD34D" fontSize="11" textAnchor="middle">{c.diag_fill}</text>
      <line x1="279" y1="78" x2="198" y2="117" stroke="#FCD34D" strokeWidth="1.8" opacity="0.6" strokeDasharray="4 3" />
      {/* rim/back light - behind (top) */}
      <circle cx="180" cy="40" r="11" fill="#60A5FA" opacity="0.8" />
      <text x="180" y="20" fill="#60A5FA" fontSize="11" textAnchor="middle">{c.diag_rim}</text>
      <line x1="180" y1="51" x2="180" y2="103" stroke="#60A5FA" strokeWidth="1.8" opacity="0.7" />
    </svg>
  );
}

export default function LightingBasicsPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `lighting-basics-${lang}.pdf`, [lang]);

  const Block = ({ title, children, accent }) => (
    <motion.div variants={itemAnim} className={`bg-[#121214]/80 backdrop-blur-md border ${accent ? "border-[#F5A524]/30" : "border-white/10"} rounded-xl p-6`}>
      <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{title}</h2>
      {children}
    </motion.div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="lighting-basics-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2 flex items-center gap-3">
                <Lightbulb weight="fill" className="w-8 h-8 text-[#F5A524]" />
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button onClick={download} disabled={!ready} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60" data-testid="lighting-basics-download-btn">
              <Printer className="w-4 h-4 mr-1.5" />{generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Diagram */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6" data-testid="lighting-basics-diagram">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-4">{c.diagram_title}</h2>
            <div className="max-w-md mx-auto">
              <ThreePointDiagram c={c} />
            </div>
            <p className="text-[#71717A] text-xs italic mt-3 leading-relaxed text-center">{c.diagram_caption}</p>
          </motion.div>

          {/* Three lights */}
          <Block title={c.three_title}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
              {c.three.map((p, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4" data-testid={`lighting-basics-light-${i}`}>
                  <h3 className="text-[#F5A524] font-medium text-sm mb-1.5">{p.n}</h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </Block>

          <Block title={c.quality_title} accent>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.quality_body}</p>
            <p className="text-[#E4E4E7] text-sm leading-relaxed bg-[#F5A524]/10 border border-[#F5A524]/20 rounded-lg p-3">{c.quality_rule}</p>
          </Block>

          <Block title={c.direction_title}>
            <ul className="space-y-2 mt-1">
              {c.direction.map((d, i) => (
                <li key={i} className="flex items-start gap-2"><span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" /><span className="text-[#A1A1AA] text-sm leading-relaxed">{d}</span></li>
              ))}
            </ul>
          </Block>

          <Block title={c.inverse_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.inverse_body}</p>
          </Block>

          <Block title={c.color_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.color_body}</p>
          </Block>

          <Block title={c.natural_title}>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.natural_body}</p>
            <p className="text-[#71717A] text-sm">
              {c.next_more}{" "}
              <Link to="/lighting" className="text-[#F5A524] hover:underline font-medium" data-testid="lighting-basics-next-link">
                {c.next_link} →
              </Link>
            </p>
          </Block>

          <motion.div variants={itemAnim} className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-xl p-4">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.pro_tip}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="lighting-basics-print">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{c.three_title}</h2>
        <div className="space-y-2 mb-4">
          {c.three.map((p, i) => (<div key={i}><p className="text-sm font-bold text-gray-800">{p.n}</p><p className="text-xs text-gray-700">{p.d}</p></div>))}
        </div>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-amber-200 pb-2 text-amber-700">{c.quality_title}</h2>
        <p className="text-sm text-gray-700 mb-1">{c.quality_body}</p>
        <p className="text-sm text-gray-800 font-medium mb-4">{c.quality_rule}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{c.direction_title}</h2>
        <ul className="list-disc ml-5 space-y-1 mb-4">{c.direction.map((d, i) => (<li key={i} className="text-xs text-gray-700">{d}</li>))}</ul>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{c.inverse_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.inverse_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{c.color_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.color_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">{c.natural_title}</h2>
        <p className="text-sm text-gray-700 mb-3">{c.natural_body}</p>
        <p className="text-xs text-gray-700 italic"><strong>Pro tip:</strong> {c.pro_tip}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
